import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import Dropzone, { DropzoneRef, FileRejection } from 'react-dropzone';
import { Label, Card, Alert, Button } from 'reactstrap';
import { renderIcon } from '~/shared/components/icons';
import { FundRequestContext } from '~/context/fundrequests';
import imageCompression from 'browser-image-compression';
import {
    deleteFundRequestItemImage,
    fetchFundRequestItemPresignedUrl,
    resizeImage,
    saveFundRequestItemImage,
    uploadFile,
} from '~/services/fundrequests';
import { TransactionUpload } from '~/types/fundrequests';
import { ImageModal } from './ImageModal';
import { Uploading } from '~/shared/components/Loading';

type FileUpload = {
    filename: string;
    id: number;
    image_url: string;
};

interface UploadDocumentProps {
    maxFileSizeMB?: number;
}

const UploadDocument = ({ maxFileSizeMB = 5 }: UploadDocumentProps) => {
    const [
        { fundRequest, expenseItem, uploadPath, formKey },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const {
        watch,
        formState: { errors },
        setValue,
    } = useFormContext();
    const [uploadedFiles, setUploadedFiles] = useState<TransactionUpload[]>([]);
    const [imageModal, setImageModal] = useState<TransactionUpload | null>(null);
    const [savingImage, setSavingImage] = useState(false);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [showExistingFiles, setShowExistingFiles] = useState(false);
    const [existingFiles, setExistingFiles] = useState<FileUpload[]>([]);
    const currentItem = formKey ? watch(formKey) : null;
    const currentExistingUploads: FileUpload[] = watch(
        `${formKey}.existing_uploads`
    );
    const dropzoneRef = useRef<DropzoneRef>(null);

    useEffect(() => {
        const uploads = expenseItem?.uploads;
        if (uploads && uploads.length > 0) {
            setUploadedFiles(uploads);
        } else {
            setUploadedFiles([]);
        }
        setShowExistingFiles(false);
        // Clear existing_uploads form field since the documents are now saved
        if (formKey) {
            setValue(`${formKey}.existing_uploads`, []);
        }
    }, [expenseItem]);

    useEffect(() => {
        if (imageModal) {
            setOpenModal(true);
        }
    }, [imageModal]);

    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        setError(''); // Clear any previous errors at the start of each drop attempt

        if (fileRejections.length > 0) {
            setError(
                `File must be a PDF or image and less than ${maxFileSizeMB} MB`
            );
            return;
        }
        if (!fundRequest?.id) {
            setError('Invalid fund request id');
        }
        if (!currentItem?.id) {
            setError('Expense item needs an id');
        }
        setSavingImage(true);

        const uploadPromises = acceptedFiles.map(async (file: File) => {
            try {
                const filenameParts = file.name.split('.');
                const ext = filenameParts[filenameParts.length - 1];
                const name = filenameParts[0]
                    .replace(/\s/g, '_')
                    .replace(/(?!\w)./g, '');
                const fileName = `${name}.${ext}`;
                const { fields, url } = await fetchFundRequestItemPresignedUrl(
                    uploadPath,
                    fileName
                );
                // Resize image files only, skip for PDFs and other document types
                const fileToUpload = await resizeImage(file);

                await uploadFile(fields, url, fileToUpload);

                const { key } = fields;
                const saveResp = await saveFundRequestItemImage(
                    uploadPath,
                    fileName,
                    url + key
                );
                return saveResp;
            } catch (error) {
                console.log(error);
                return null;
            }
        });
        Promise.all(uploadPromises).then((results) => {
            const successfulUploads = results.filter(
                (result) => result !== null
            );
            setUploadedFiles((prevUploadedFiles) => [
                ...prevUploadedFiles,
                ...successfulUploads,
            ]);

            fundRequestDispatch({
                type: 'set_uploads',
                payload: [...uploadedFiles, ...successfulUploads]
            });

            setSavingImage(false);
        });
    };

    const deleteUpload = async (upload: TransactionUpload) => {
        const deletePath = `${uploadPath}/${upload.id}`;
        const updatedFiles = uploadedFiles.filter(
            (file) => file.image_url !== upload.image_url
        );
        fundRequestDispatch({
            type: 'set_uploads',
            payload: updatedFiles,
        });
        await deleteFundRequestItemImage(deletePath);
    };

    const getAllUploads = () => {
        const allExpenseItems = [
            ...fundRequest.transactions_fundraising,
            ...fundRequest.transactions_medical,
            ...fundRequest.transactions_medications,
            ...fundRequest.transactions_insurance,
            ...fundRequest.transactions_relocation_lodging,
            ...fundRequest.transactions_other,
            ...fundRequest.transactions_travel,
            ...fundRequest.transactions_food,
            ...fundRequest.transactions_physical_therapy,
            ...fundRequest.transactions_caregiving,
        ];

        // Flatten all uploads from each transaction
        let uniqueFilenames: string[] = [];
        const allUploads = allExpenseItems
            .flatMap((transaction) => transaction.uploads || [])
            .map((upload) => {
                if (!uniqueFilenames.includes(upload.filename)) {
                    uniqueFilenames.push(upload.filename);
                    return upload;
                }
                return null;
            })
            .filter((upload) => upload !== null);
        setExistingFiles(allUploads as FileUpload[]);
        return allUploads;
    };

    useEffect(() => {
        getAllUploads();
    }, []);

    const handleDropzoneClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest('button')) {
            event.stopPropagation();
            setShowExistingFiles(true);
        }
    };
    const getButtonStyles = (isSelected: boolean) => ({
        backgroundColor: isSelected ? '#01aeb4' : 'transparent',
        color: 'black',
        borderColor: isSelected ? '#01aeb4' : 'black',
    });

    const handleExistingFileClick = (item: FileUpload) => {
        const itemFound = isItemSelected(item);
        if (itemFound) {
            const filterItems = currentExistingUploads.filter(
                (selectedItem) => selectedItem.id !== item.id
            );
            setValue(`${formKey}.existing_uploads`, filterItems, { shouldDirty: true });
        } else {
            const newList = [
                ...(currentExistingUploads || []),
                item,
            ];
            setValue(`${formKey}.existing_uploads`, newList, { shouldDirty: true });
        }
    };

    const isItemSelected = (item: FileUpload) => {
        if (!currentExistingUploads) {
            return false;
        }

        const itemFound = currentExistingUploads?.find(
            (selectedItem) => selectedItem.id === item.id
        );

        return Boolean(itemFound);
    };

    const cancelUseExisting = () => {
        setShowExistingFiles(false);
        setValue(`${formKey}.existing_uploads`, []);
    };

    const confirmExistingSelection = () => {
        setShowExistingFiles(false);
    };

    const removeExistingUpload = (item: FileUpload) => {
        const filterItems = currentExistingUploads.filter(
            (selectedItem) => selectedItem.id !== item.id
        );
        setValue(`${formKey}.existing_uploads`, filterItems);
    };
    return (
        <>
            <Label className='pt-2'>
                Add Supporting Documents <span className='required'>*</span>
            </Label>
            <Card
                style={{
                    border: '2px dashed #949599',
                    padding: '20px',
                    backgroundColor: '#F0F6F6',
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '8px',
                }}
            >
                {showExistingFiles && (
                    <div>
                        <p>Select from existing files:</p>
                        <div className='d-flex justify-content-center gap-1 flex-wrap'>
                            {existingFiles.map((item) => {
                                return (
                                    <Button
                                        key={item.id}
                                        onClick={() =>
                                            handleExistingFileClick(item)
                                        }
                                        size='sm'
                                        style={{
                                            ...getButtonStyles(
                                                isItemSelected(item)
                                            ),
                                            fontSize: '0.8rem',
                                            textTransform: 'none',
                                            padding: '5px 15px',
                                            margin: 0,
                                        }}
                                    >
                                        {item.filename}
                                    </Button>
                                );
                            })}
                        </div>
                        <div className='d-flex justify-content-center gap-3'>
                            <button
                                onClick={cancelUseExisting}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: '#888888',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmExistingSelection}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: '#23b9bd',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                    fontWeight: currentExistingUploads?.length > 0 ? 'bold' : 'normal',
                                }}
                                disabled={!currentExistingUploads?.length}
                            >
                                Done {currentExistingUploads?.length > 0 && `(${currentExistingUploads.length})`}
                            </button>
                        </div>
                    </div>
                )}
                {!showExistingFiles && (
                    <Dropzone
                        key={error ? 'error' : 'normal'}
                        onDrop={(acceptedFiles, fileRejections) =>
                            handleDrop(acceptedFiles, fileRejections)
                        }
                        onDragEnter={() => setError('')}
                        onDragOver={() => setError('')}
                        accept={{
                            'application/pdf': ['.pdf'],
                            'image/jpeg': ['.jpeg', '.jpg'],
                            'image/png': ['.png']
                        }}
                        multiple={true}
                        ref={dropzoneRef}
                    >
                        {({ getRootProps, getInputProps }) => {
                            const inputProps = getInputProps();

                            return (
                                <div {...getRootProps()} onClick={() => setError('')}>
                                    <input {...inputProps} />
                                    <div className='d-flex flex-column flex-sm-row gap-2 align-items-center justify-content-center'>
                                        {existingFiles.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowExistingFiles(true);
                                                }}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: '#23b9bd',
                                                }}
                                            >
                                                Use Existing Document
                                            </button>
                                        )}

                                        {savingImage ? (
                                            <Uploading />
                                        ) : (
                                            <Button
                                                color='outline-dark'
                                                size='sm'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setError(''); // Clear error when clicking upload button
                                                    dropzoneRef.current?.open();
                                                }}
                                            >
                                                {renderIcon('add')} Upload New
                                            </Button>
                                        )}
                                    </div>
                                    <p
                                        className='small'
                                        style={{
                                            margin: '10px 0',
                                            color: '#888888',
                                        }}
                                    >
                                        Drag & drop files here, or click to select files
                                    </p>
                                    <p
                                        className='small'
                                        style={{
                                            margin: '10px 0',
                                            color: '#888888',
                                        }}
                                    >
                                        Maximum upload file size: {maxFileSizeMB} MB
                                    </p>
                                </div>
                            );
                        }}
                    </Dropzone>
                )}
                {error && (
                    <Alert color='danger' className='mt-2'>
                        {error}
                    </Alert>
                )}
                {errors?.files && (
                    <Alert color='danger' className='mt-2'>
                        {errors?.files?.message}
                    </Alert>
                )}
                {!showExistingFiles && uploadedFiles.length > 0 && (
                    <div className='d-flex justify-content-center gap-1 flex-wrap'>
                        {uploadedFiles.map((upload, index) => (
                            <div
                                key={index}
                                className='d-inline-flex justify-items-center align-items-center gap-2 rounded bg-primary px-2 py-1'
                                style={{ fontSize: '13px' }}
                            >
                                <div
                                    onClick={() => {
                                        const thisImage = uploadedFiles.find(
                                            (file) => file.id === upload.id
                                        );
                                        if (thisImage) setImageModal(thisImage);
                                    }}
                                >
                                    {upload.filename}
                                </div>
                                <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => deleteUpload(upload)}
                                >
                                    {renderIcon('close')}
                                </span>
                            </div>
                        ))}
                        <ImageModal
                            isOpen={openModal}
                            image_url={imageModal?.image_url}
                            filename={imageModal?.filename}
                            toggle={() => {
                                setOpenModal(!openModal);
                                setImageModal(null);
                            }}
                        />
                    </div>
                )}
                {!showExistingFiles && currentExistingUploads?.length > 0 && (
                    <div className='mt-2'>
                        <p className='small mb-1' style={{ color: '#888888' }}>
                            Existing documents to add (will be saved when you save the expense):
                        </p>
                        <div className='d-flex justify-content-center gap-1 flex-wrap'>
                            {currentExistingUploads.map((upload, index) => (
                                <div
                                    key={`existing-${upload.id}-${index}`}
                                    className='d-inline-flex justify-items-center align-items-center gap-2 rounded px-2 py-1'
                                    style={{ 
                                        fontSize: '13px',
                                        backgroundColor: '#01aeb4',
                                        color: 'white'
                                    }}
                                >
                                    <div>{upload.filename}</div>
                                    <span
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => removeExistingUpload(upload)}
                                    >
                                        {renderIcon('close')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>
        </>
    );
};

export default UploadDocument;
